import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification } from "hooks/useNotification";
import { Uppdrag } from "types/uppdrag.type";
import {
  uppdragSchema,
  TUppdragSchema,
} from "pages/app/uppdrag/overview/uppdragSchema";
import { patchUppdrag, UppdragQueryKey } from "api/uppdrag.api";
import { addDays, startOfMonth, startOfWeek } from "date-fns";
import { useCurrentCompany } from "api/company.api";
import { Role } from "types/enums";
import { useUsers } from "api/user.api";
import { TransportSedelQueryKey } from "api/transportSedel.api";

type Indata = {
  uppdragToEdit: Uppdrag;
};
export const usePatchUppdragLogic = ({ uppdragToEdit }: Indata) => {
  const { data: currentCompany } = useCurrentCompany();

  const { data: users } = useUsers({}, { roles: [Role.Operator, Role.Admin] });
  const userDropdownItems = useMemo(
    () =>
      !currentCompany
        ? []
        : (users ?? [])
            .filter(
              (u) =>
                u.role === Role.Operator ||
                currentCompany.settings.isUppdragSchedulingToAdminActivated,
            )
            .map((x) => ({ label: x.name, value: x.uuid }))
            .sort((a, b) => (a.label < b.label ? -1 : 1)),
    [users, currentCompany],
  );

  const queryClient = useQueryClient();
  const notification = useNotification();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<TUppdragSchema>({
    resolver: zodResolver(uppdragSchema),
  });

  const resetForm = useCallback(
    (uppdrag?: Uppdrag) => {
      uppdrag
        ? form.reset({
            assignedToUserUuid: uppdrag.assignedToUser?.uuid,
            scheduleDate: !uppdrag.scheduleDate
              ? undefined
              : new Date(uppdrag.scheduleDate),
            scheduleDateType: uppdrag.scheduleDateType,
            description: uppdrag.description,
            isPrioritized: uppdrag.isPrioritized,
            referenceNumber: uppdrag.referenceNumber,
          })
        : form.reset();
    },
    [form],
  );

  useEffect(() => {
    if (uppdragToEdit) {
      resetForm(uppdragToEdit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uppdragToEdit]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "scheduleDateType" && value.scheduleDate) {
        const date = new Date(value.scheduleDate);
        const offset = date.getTimezoneOffset() * 60000; // Convert offset to milliseconds

        let newDate;
        switch (value.scheduleDateType) {
          case "Week":
            let weekStart = startOfWeek(date, { weekStartsOn: 1 });
            // If week starts in previous month, move to next week
            if (weekStart.getMonth() !== date.getMonth()) {
              weekStart = addDays(weekStart, 7);
            }
            newDate = weekStart;
            break;
          case "Month":
            newDate = startOfMonth(date);
            break;
        }

        // Adjust for timezone
        if (!newDate) return;
        form.setValue("scheduleDate", new Date(newDate.getTime() - offset));
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const onModalClose = () => setIsModalOpen(false);
  const afterModalClose = () => resetForm();

  const patchMutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });

      onModalClose();
    },
    onError: () => notification.error(),
  });

  const onSubmit = form.handleSubmit((data: TUppdragSchema) => {
    patchMutation.mutate({
      id: uppdragToEdit.id,
      payload: data,
    });
  });

  const scheduleDate = form.watch("scheduleDate");
  const scheduleDateType = form.watch("scheduleDateType");

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    form,
    onSubmit,
    mutation: patchMutation,
    onModalClose,
    afterModalClose,
    uppdrag: uppdragToEdit,
    modalTitle: "Ändra uppdrag",
    scheduleDate,
    scheduleDateType,
    userDropdownItems,
  };
};

export type CreateOrUpdateUppdragLogicRetVal = ReturnType<
  typeof usePatchUppdragLogic
>;
